import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  AppBar, Avatar, Box, Collapse, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText,
  ListSubheader, Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import logo from "../assets/cloudforge-logo.png";
import { useAuth } from "../auth/AuthProvider";
import { ImpersonationBanner } from "../console_impersonation/ImpersonationBanner";
import { HelpMenu } from "../help_center/HelpMenu";
import { SubscriptionBanner } from "./SubscriptionBanner";
import { ThemeToggle } from "./ThemeToggle";
import { MaintenanceBanner } from "../hub_maintenance_mode/MaintenanceMode";
import { SearchPalette } from "../search_global_search/SearchPalette";
import { NotificationBell } from "../workflow_notifications/NotificationCenter";
import { navSections, type NavEntry } from "./navigation";
import { useFavorites } from "./useFavorites";

export const DRAWER_WIDTH = 272;
const NAV_ICON_SIZE = 26;
const NAV_TEXT = { fontSize: 16, fontWeight: 500 } as const;

function useVisibleSections() {
  const { me, hasPermission } = useAuth();
  return useMemo(() => {
    if (!me) return [];
    const installed = new Set(me.spokes.map((s) => String(s.label)));
    const ent = (me as unknown as { entitlements?: { modules?: string[] } }).entitlements;
    const modules = new Set(ent?.modules ?? ["*"]);
    const entitled = (spoke: string) => modules.has("*") || modules.has(spoke) || ["hub", "accounts", "audit", "search", "billing"].includes(spoke);
    const allowed = (entry: NavEntry) => {
      if (entry.flag && !me.flags?.[entry.flag]) return false;
      if (!entry.permission) return true;
      const codes = Array.isArray(entry.permission) ? entry.permission : [entry.permission];
      return codes.some(hasPermission);
    };
    return navSections()
      .filter((s) => (installed.has(s.spoke) || s.spoke === "hub") && entitled(s.spoke))
      .map((s) => ({ ...s, entries: s.entries.filter(allowed) }))
      .filter((s) => s.entries.length > 0);
  }, [me, hasPermission]);
}

function NavItem({ entry, selected, onNavigate, favorite, onToggleFavorite, indent }: {
  entry: NavEntry; selected: boolean; onNavigate?: () => void; favorite: boolean; onToggleFavorite: () => void; indent?: boolean;
}) {
  const { t } = useTranslation();
  const starLabel = favorite ? t("nav.removeFavorite", { item: entry.label }) : t("nav.addFavorite", { item: entry.label });
  return (
    <ListItemButton component={NavLink} to={entry.to} onClick={onNavigate} selected={selected}
      sx={{ pl: indent ? 3 : 2, pr: 5, minHeight: 44, "&:hover .nav-star, &:focus-within .nav-star, & .nav-star.is-favorite": { opacity: 1 } }}>
      {entry.icon && <ListItemIcon sx={{ minWidth: 40 }}><entry.icon /></ListItemIcon>}
      <ListItemText primary={entry.label} primaryTypographyProps={NAV_TEXT} />
      <Tooltip title={starLabel}>
        <IconButton size="small" aria-label={starLabel} aria-pressed={favorite}
          className={`nav-star${favorite ? " is-favorite" : ""}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(); }}
          sx={{ position: "absolute", right: 4, opacity: 0, transition: "opacity 120ms", color: favorite ? "warning.main" : "text.secondary", "& .MuiSvgIcon-root": { fontSize: 20 } }}>
          {favorite ? <StarIcon /> : <StarBorderIcon />}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const sections = useVisibleSections();
  const location = useLocation();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const { t } = useTranslation();
  const { favorites, isFavorite, toggle } = useFavorites();
  const favoriteEntries = useMemo(() => {
    const byPath = new Map(sections.flatMap((s) => s.entries.map((e) => [e.to, e] as const)));
    return favorites.map((to) => byPath.get(to)).filter((e): e is NavEntry => !!e);
  }, [sections, favorites]);
  return (
    <List component="nav" aria-label="Main navigation" sx={{ px: 1, "& .MuiSvgIcon-root": { fontSize: NAV_ICON_SIZE } }}>
      <ListItemButton component={NavLink} to="/app" end onClick={onNavigate}
        selected={location.pathname === "/app"}>
        <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon /></ListItemIcon>
        <ListItemText primary={t("nav.home")} primaryTypographyProps={NAV_TEXT} />
      </ListItemButton>
      {favoriteEntries.length > 0 && (
        <Box component="section" aria-label={t("nav.favorites")}>
          <ListSubheader disableSticky component="div"
            sx={{ lineHeight: "40px", fontSize: 14, fontWeight: 600, bgcolor: "transparent", display: "flex", alignItems: "center" }}>
            <StarIcon sx={{ mr: 1.5, color: "warning.main" }} />
            {t("nav.favorites")}
          </ListSubheader>
          {favoriteEntries.map((entry) => (
            <NavItem key={`fav:${entry.to}`} entry={entry} selected={location.pathname.startsWith(entry.to)} onNavigate={onNavigate}
              favorite onToggleFavorite={() => toggle(entry.to)} />
          ))}
          <Divider sx={{ my: 1 }} />
        </Box>
      )}
      {sections.map((section) => {
        const isOpen = open[section.key] ?? section.entries.some((e) => location.pathname.startsWith(e.to));
        const Icon = section.icon;
        return (
          <Box key={section.key}>
            <ListSubheader disableSticky component="div"
              sx={{ cursor: "pointer", lineHeight: "40px", fontSize: 14, fontWeight: 600, bgcolor: "transparent", display: "flex", alignItems: "center" }}
              onClick={() => setOpen((o) => ({ ...o, [section.key]: !isOpen }))}>
              {Icon && <Icon sx={{ mr: 1.5 }} />}
              {section.title}
            </ListSubheader>
            <Collapse in={isOpen}>
              {section.entries.map((entry) => (
                <NavItem key={entry.to} entry={entry} selected={location.pathname.startsWith(entry.to)} onNavigate={onNavigate} indent
                  favorite={isFavorite(entry.to)} onToggleFavorite={() => toggle(entry.to)} />
              ))}
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}

export function AppShell() {
  const { me, logout, switchOrganization } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const [orgAnchor, setOrgAnchor] = useState<HTMLElement | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const { t } = useTranslation();

  const drawer = (
    <Box role="presentation" sx={{ width: DRAWER_WIDTH }}>
      <Toolbar variant="dense" sx={{ px: 2, gap: 1.5 }}>
        <Box component="img" src={logo} alt="" sx={{ width: 32, height: 32, borderRadius: 1 }} />
        <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>{t("app.name")}</Typography>
      </Toolbar>
      <Divider />
      <NavList onNavigate={() => setMobileOpen(false)} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <a href="#main" className="skip-link">{t("app.skipToContent")}</a>
      <AppBar position="fixed" color="inherit" elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider", zIndex: (th) => th.zIndex.drawer + 1 }}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          {isMobile && (
            <IconButton edge="start" aria-label="Open navigation" onClick={() => setMobileOpen(true)}><MenuIcon /></IconButton>
          )}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1.5, minWidth: DRAWER_WIDTH - 24 }}>
            <Box component="img" src={logo} alt="" sx={{ width: 32, height: 32, borderRadius: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("app.name")}</Typography>
          </Box>
          <Tooltip title={`${t("nav.search")} (Ctrl+K)`}>
            <IconButton aria-label={t("nav.search")} onClick={() => setSearchOpen(true)}><SearchIcon /></IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          {me && me.organizations.length > 0 && (
            <>
              <Tooltip title={t("nav.switchOrganization")}>
                <Typography variant="body2" sx={{ cursor: "pointer", fontWeight: 600 }} onClick={(e) => setOrgAnchor(e.currentTarget)}>
                  {me.active_organization?.name ?? "—"}
                </Typography>
              </Tooltip>
              <Menu open={!!orgAnchor} anchorEl={orgAnchor} onClose={() => setOrgAnchor(null)}>
                {me.organizations.map((o) => (
                  <MenuItem key={o.slug} selected={o.slug === me.active_organization?.slug}
                    onClick={() => { setOrgAnchor(null); switchOrganization(o.slug); }}>{o.name}</MenuItem>
                ))}
              </Menu>
            </>
          )}
          <ThemeToggle />
          <NotificationBell />
          <HelpMenu trigger={(open) => (
            <Tooltip title={t("nav.help")}><IconButton aria-label={t("nav.help")} onClick={open}><HelpOutlineIcon /></IconButton></Tooltip>
          )} />
          <IconButton aria-label="Account menu" onClick={(e) => setUserAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{(me?.first_name?.[0] ?? me?.email?.[0] ?? "?").toUpperCase()}</Avatar>
          </IconButton>
          <Menu open={!!userAnchor} anchorEl={userAnchor} onClose={() => setUserAnchor(null)}>
            <MenuItem disabled>{me?.email}</MenuItem>
            <MenuItem component={NavLink} to="/app/settings/profile" onClick={() => setUserAnchor(null)}>{t("nav.settings")}</MenuItem>
            {me?.is_staff && <MenuItem component={NavLink} to="/console" onClick={() => setUserAnchor(null)}>Provider console</MenuItem>}
            <MenuItem onClick={() => { setUserAnchor(null); void logout(); }}>{t("nav.signOut")}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {isMobile ? (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}>
            {drawer}
          </Drawer>
        ) : (
          <Drawer variant="permanent" open sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}>
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box component="main" id="main" tabIndex={-1} sx={{ flexGrow: 1, minWidth: 0, outline: "none" }}>
        <Toolbar variant="dense" />
        <ImpersonationBanner />
        <SubscriptionBanner />
        <MaintenanceBanner />
        <Box sx={{ p: 3 }}><Outlet /></Box>
      </Box>
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} onOpen={() => setSearchOpen(true)} />
    </Box>
  );
}
